import { Col, Row } from "antd";
import { useFormik } from "formik";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineFace,
  MdOutlineFace2,
  MdOutlineFemale,
  MdOutlineMale,
} from "react-icons/md";
import { NAME_SPACES } from "shared/locales/constants";
import { AUTHORITY_TYPE, GENDER } from "utils/constants";
import {
  bindInputProps,
  bindWebTextFieldInputProps,
} from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Card from "../Components/Card";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import validation from "./validation";

const NUMBERS_MAPPING = {
  1: "1er",
  2: "2ème",
  3: "3ème",
  4: "4ème",
  5: "5ème",
  6: "6ème",
  7: "7ème",
  8: "8ème",
  9: "9ème",
  10: "10ème",
  11: "11ème",
  12: "12ème",
};

function View({ children, spouse, onNext, onBack }) {
  const { t } = useTranslation();
  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues: { children, spouse },
    onSubmit: onNext,
    key: JSON.stringify(children, spouse),
    enableReinitialize: true,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      { hasPartner: !isEmpty(spouse) },
    ),
  });

  const getAge = (birthdate) => {
    if (!birthdate) return 0;
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <Layout>
      <Header
        title="Indiquez les informations nécessaires pour couvrir votre foyer"
        highlight="votre foyer"
        subtitle="Informations du foyer"
        onBack={onBack}
      />
      <Row justify="center">
        <Col xs={24} md={12}>
          <div className="grid--1">
            {!isEmpty(spouse) && (
              <Card bg="white">
                <div className="heading__amellis">
                  Informations de{" "}
                  <span className="highlight">votre conjoint</span>
                </div>
                <div className="grid--1">
                  <Input
                    row
                    size="full"
                    label={"Civilité"}
                    type="radio"
                    values={[
                      {
                        icon: <MdOutlineFace size={24} />,
                        value: GENDER.MALE,
                        label: "Homme",
                      },
                      {
                        icon: <MdOutlineFace2 size={24} />,
                        value: GENDER.FEMALE,
                        label: "Femme",
                      },
                    ]}
                    {...bindInputProps({
                      name: "spouse.identity.user.gender",
                      values,
                      ...formProps,
                    })}
                    onChange={(value) =>
                      setFieldValue("spouse.identity.user.gender", value)
                    }
                  />
                  <div className="grid--2">
                    <Input
                      placeholder={"ex : Doe"}
                      label={"Nom"}
                      {...bindWebTextFieldInputProps({
                        name: "spouse.identity.user.lastname",
                        values,
                        setFieldValue,
                        ...formProps,
                      })}
                    />
                    <Input
                      placeholder={"ex : John"}
                      label={"Prénom"}
                      {...bindWebTextFieldInputProps({
                        name: "spouse.identity.user.firstname",
                        values,
                        setFieldValue,
                        ...formProps,
                      })}
                    />
                  </div>
                  <Input
                    placeholder={"ex : 185057800608436"}
                    label={"N° de sécurité sociale"}
                    trim={true}
                    {...bindInputProps({
                      name: "spouse.identity.socialSecurityNumber",
                      values,
                      ...formProps,
                    })}
                  />
                </div>
              </Card>
            )}
            {children.map((_, i) => {
              const age = getAge(
                values.children?.[i]?.identity?.user?.birthDate,
              );
              const isMinor = age <= 18;

              return (
                <Card bg="white" key={`card_${i}`}>
                  <div className="heading__amellis">
                    <>
                      Informations de votre{" "}
                      <span className="highlight">
                        {NUMBERS_MAPPING[i + 1]} enfant
                      </span>
                    </>
                  </div>
                  <div className="grid--1">
                    <Input
                      row
                      label={"Civilité"}
                      size="full"
                      type="radio"
                      values={[
                        {
                          icon: <MdOutlineMale size={24} />,
                          value: GENDER.MALE,
                          label: "Masculin",
                        },
                        {
                          icon: <MdOutlineFemale size={24} />,
                          value: GENDER.FEMALE,
                          label: "Féminin",
                        },
                      ]}
                      {...bindInputProps({
                        name: `children.${i}.identity.user.gender`,
                        values,
                        ...formProps,
                      })}
                      onChange={(value) =>
                        setFieldValue(
                          `children.${i}.identity.user.gender`,
                          value,
                        )
                      }
                    />
                    <div className="grid--2">
                      <Input
                        placeholder={"ex : Doe"}
                        label={"Nom"}
                        {...bindWebTextFieldInputProps({
                          name: `children.${i}.identity.user.lastname`,
                          values,
                          setFieldValue,
                          ...formProps,
                        })}
                      />
                      <Input
                        placeholder={"ex : John"}
                        label={"Prénom"}
                        {...bindWebTextFieldInputProps({
                          name: `children.${i}.identity.user.firstname`,
                          values,
                          setFieldValue,
                          ...formProps,
                        })}
                      />
                    </div>

                    {!isMinor ? (
                      <Input
                        placeholder={"ex : 185057800608436"}
                        label={"N° de sécurité sociale"}
                        trim={true}
                        {...bindInputProps({
                          name: `children.${i}.identity.socialSecurityNumber`,
                          values,
                          ...formProps,
                        })}
                      />
                    ) : (
                      <Input
                        row
                        label={"Rattachement"}
                        type="radio"
                        size="full"
                        values={[
                          {
                            icon: <MdOutlineFace size={24} />,
                            value: AUTHORITY_TYPE.FATHER,
                            label: "Père",
                          },
                          {
                            icon: <MdOutlineFace2 size={24} />,
                            value: AUTHORITY_TYPE.MOTHER,
                            label: "Mère",
                          },
                          {
                            value: AUTHORITY_TYPE.BOTH,
                            label: "Les deux",
                          },
                        ]}
                        {...bindInputProps({
                          name: `children.${i}.authority`,
                          values,
                          ...formProps,
                        })}
                        onChange={(value) =>
                          setFieldValue(`children.${i}.authority`, value)
                        }
                      />
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </Col>
      </Row>

      <ActionButtons
        disabled={!isEmpty(formProps.errors)}
        onNext={handleSubmit}
        onBack={onBack}
      />
    </Layout>
  );
}

export default View;
